/**
 * The Netly Pay API, as the dashboard reaches it.
 *
 * Netly Pay runs in its own stack with its own Cognito pool (D2), so the
 * dashboard cannot call the owner routes directly — it has no Netly Pay user.
 * Instead the Pay API exposes `/netlise/v1/*`, which accepts a Netlise
 * dashboard JWT plus an `X-Netlise-Shop-Id` header, resolves the workspace
 * from the shop link, and then calls the same service functions as the owner
 * routes (D54, CONTRACT-NETLISE.md). There is no second login.
 *
 * The workspace is NEVER sent by this client. It is resolved server-side from
 * the link, because a header a browser can set must not choose whose members
 * come back.
 *
 * `not_linked` is the response that matters most: it is not an error, it means
 * this shop has not set Netly Pay up, and it is what makes the dashboard show
 * the Enable screen. Callers branch on `err.code === 'not_linked'`, which is
 * why these functions throw a typed error rather than returning null.
 *
 * Amounts are in MINOR units both ways, matching NetlyPayManager.
 */
import {Auth} from '@aws-amplify/auth';
import type {
  Approval,
  Batch,
  Member,
  PaymentRequest,
  Program,
  Session,
} from '../managers/NetlyPayManager';

export const NETLYPAY_API_URL =
  process.env.REACT_APP_NETLYPAY_API_URL ||
  'https://e7x1cg9wyd.execute-api.ap-southeast-1.amazonaws.com/v1';

/** The dashboard prefix. The mobile app's own routes sit at the root. */
const PREFIX = '/netlise/v1';

/**
 * An error that carries the API's own code.
 *
 * The codes the dashboard branches on are in CONTRACT-NETLISE.md §1.2:
 * `not_linked` (409, show Enable), `forbidden` (403, hide the control),
 * `not_found` (404), `already_linked`, `workspace_taken`, `owner_mismatch`.
 */
export class NetlyPayError extends Error {
  code: string;

  status: number;

  detail: any;

  constructor(code: string, status: number, message: string, detail?: any) {
    super(message);
    this.name = 'NetlyPayError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

/** True when this shop has no workspace yet — the Enable screen's cue. */
export function isNotLinked(err: unknown): boolean {
  return err instanceof NetlyPayError && err.code === 'not_linked';
}

async function idToken(): Promise<string | null> {
  try {
    return (await Auth.currentSession()).getIdToken().getJwtToken();
  } catch (e) {
    // Signed out, or the session expired mid-page. The caller surfaces this
    // as the generic error state; the app-wide auth guard handles the redirect.
    return null;
  }
}

async function request(path: string, shopId: string, init?: RequestInit): Promise<any> {
  if (!shopId) {
    throw new NetlyPayError('shop_id_required', 400, 'No shop selected');
  }

  const token = await idToken();
  const res = await fetch(`${NETLYPAY_API_URL}${PREFIX}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Netlise-Shop-Id': shopId,
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
      ...(init?.headers ?? {}),
    },
  });

  const text = await res.text();
  let body: any = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch (e) {
    // A gateway timeout or a 502 returns HTML. Do not let a parse error
    // masquerade as an API error code.
    if (!res.ok) throw new NetlyPayError('unavailable', res.status, 'Netly Pay is not responding');
    throw new NetlyPayError('bad_response', res.status, 'Unexpected response from Netly Pay');
  }

  if (!res.ok) {
    throw new NetlyPayError(
      body?.error || 'error',
      res.status,
      body?.message || body?.error || `Netly Pay error (${res.status})`,
      body,
    );
  }
  return body;
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join('&')}` : '';
};

// ── Link ────────────────────────────────────────────────────────────────────

export interface Workspace {
  id: string;
  name: string;
  template: string;
  currency: string;
  netliseTenantId?: string;
}

export type LinkState =
  | {linked: false}
  | {linked: true; workspace: Workspace; role: 'OWNER' | 'STAFF'};

export interface CreateWorkspaceInput {
  mode: 'create';
  name: string;
  template: 'academy' | 'tuition' | 'tutor' | 'gym';
  ownerPhone?: string;
  currency?: string;
  methods: {
    bank?: {bank: string; branch: string; name: string; number: string} | null;
    lankaqr?: boolean;
    cash?: boolean;
  };
}

export interface LinkWorkspaceInput {
  mode: 'link';
  workspaceId: string;
  ownerPhone?: string;
}

// ── Lists ───────────────────────────────────────────────────────────────────

export interface MembersPage {
  members: Member[];
  programs: Program[];
  batches: Batch[];
  next?: string | null;
}

export interface OverviewPayload {
  collectedMinor: number;
  expectedMinor: number;
  overdue: {amountMinor: number; requests: number; members: number};
  waiting: {count: number; amountMinor: number};
  /** Monday → collected that week, for the 8-week chart. */
  weeks: Record<string, number>;
  sessions: Session[];
  activity: {ts: string; kind: string; text: string; amountMinor: number | null}[];
  batches: Batch[];
  programs: Program[];
  members: Member[];
}

const NetlyPayRepository = {
  /** Does this shop have a workspace, and what is my role in it? */
  async getLink(shopId: string): Promise<LinkState> {
    return request('/link', shopId);
  },

  /** Create a workspace for the shop, or attach one that exists. Owner only. */
  async createLink(
    shopId: string,
    input: CreateWorkspaceInput | LinkWorkspaceInput,
  ): Promise<LinkState> {
    return request('/link', shopId, {method: 'POST', body: JSON.stringify(input)});
  },

  /**
   * Stop the dashboard reading the workspace. Owner only.
   *
   * Members, requests, payments and history stay (D53) — this is not a delete,
   * and relinking the same workspace restores the view.
   */
  async removeLink(shopId: string): Promise<{linked: false}> {
    return request('/link', shopId, {method: 'DELETE'});
  },

  // ── Overview ──────────────────────────────────────────────────────────────

  async overview(shopId: string): Promise<OverviewPayload> {
    return request('/home', shopId);
  },

  // ── Members ───────────────────────────────────────────────────────────────

  async listMembers(
    shopId: string,
    opts: {q?: string; programId?: string; batchId?: string; status?: string; cursor?: string} = {},
  ): Promise<MembersPage> {
    return request(
      `/people${qs({
        q: opts.q,
        programId: opts.programId === 'all' ? undefined : opts.programId,
        batchId: opts.batchId === 'all' ? undefined : opts.batchId,
        status: opts.status === 'all' ? undefined : opts.status,
        cursor: opts.cursor,
      })}`,
      shopId,
    );
  },

  async getMember(shopId: string, memberId: string): Promise<{member: Member; requests: PaymentRequest[]}> {
    return request(`/people/${encodeURIComponent(memberId)}`, shopId);
  },

  async getMemberTimeline(shopId: string, memberId: string): Promise<{entries: any[]}> {
    return request(`/people/${encodeURIComponent(memberId)}/timeline`, shopId);
  },

  async createMember(shopId: string, member: Partial<Member>): Promise<{member: Member}> {
    return request('/people', shopId, {method: 'POST', body: JSON.stringify(member)});
  },

  async updateMember(
    shopId: string,
    memberId: string,
    patch: Partial<Member>,
  ): Promise<{member: Member}> {
    return request(`/people/${encodeURIComponent(memberId)}`, shopId, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  /**
   * Fold one EXISTING member into another, moving their requests across.
   *
   * The API takes the id of the member being absorbed, not a set of field
   * choices: this is for two rows that both exist. The duplicate warning shown
   * while ADDING someone is a different thing — nothing has been created yet,
   * so applying the chosen fields is an ordinary update of the member who is
   * already there.
   */
  async mergeMember(
    shopId: string,
    keepId: string,
    otherPersonId: string,
  ): Promise<{member: Member; moved?: number}> {
    return request(`/people/${encodeURIComponent(keepId)}/merge`, shopId, {
      method: 'POST',
      body: JSON.stringify({otherPersonId}),
    });
  },

  async freezeMember(
    shopId: string,
    memberId: string,
    body: {frozen: boolean; note?: string; until?: string},
  ): Promise<{member: Member}> {
    return request(`/people/${encodeURIComponent(memberId)}/freeze`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** Approve or reject someone who joined from the invite link. */
  async decideMember(
    shopId: string,
    memberId: string,
    decision: 'approve' | 'reject',
    body: {reason?: string} = {},
  ): Promise<{member: Member}> {
    return request(`/people/${encodeURIComponent(memberId)}/${decision}`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // ── Requests ──────────────────────────────────────────────────────────────

  async listRequests(
    shopId: string,
    opts: {status?: string; memberId?: string; cursor?: string} = {},
  ): Promise<{requests: PaymentRequest[]; next?: string | null}> {
    return request(`/requests${qs(opts as any)}`, shopId);
  },

  async getRequest(shopId: string, id: string): Promise<{request: PaymentRequest}> {
    return request(`/requests/${encodeURIComponent(id)}`, shopId);
  },

  /** One request, or one per recipient when addressed to a batch or program. */
  async createRequest(shopId: string, body: any): Promise<{requests: PaymentRequest[]}> {
    return request('/requests', shopId, {method: 'POST', body: JSON.stringify(body)});
  },

  async remind(shopId: string, body: {requestIds?: string[]; memberIds?: string[]}): Promise<any> {
    return request('/requests/remind', shopId, {method: 'POST', body: JSON.stringify(body)});
  },

  async cancelRequest(shopId: string, id: string, body: {reason?: string} = {}): Promise<any> {
    return request(`/requests/${encodeURIComponent(id)}/cancel`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // A credit note has NO route on the Pay API today. `POST /requests/{id}/credit`
  // was written against the design brief rather than the API, and would 404 —
  // the screen disables the action and says so instead of pretending.

  /** The receipt PDF's signed URL, for a paid request. */
  async receipt(shopId: string, id: string): Promise<{url: string}> {
    return request(`/requests/${encodeURIComponent(id)}/receipt`, shopId);
  },

  // ── Approvals ─────────────────────────────────────────────────────────────

  /** The queue, oldest first — the order the design asks staff to work in. */
  async listPending(shopId: string): Promise<{payments: Approval[]}> {
    return request('/payments/pending', shopId);
  },

  /**
   * Accept a proof. `amountMinor` approves a different figure from the one
   * claimed: less than owed leaves the request partly paid, more is held as
   * credit (D48).
   */
  async verifyPayment(
    shopId: string,
    paymentId: string,
    body: {amountMinor?: number; note?: string} = {},
  ): Promise<any> {
    return request(`/payments/${encodeURIComponent(paymentId)}/verify`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async rejectPayment(
    shopId: string,
    paymentId: string,
    body: {reasonCode: string; note?: string},
  ): Promise<any> {
    return request(`/payments/${encodeURIComponent(paymentId)}/reject`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Approve several at once.
   *
   * When two proofs pay the same request, the API approves the first and
   * leaves the rest — which is why the confirmation lists every line.
   */
  async verifyBulk(shopId: string, paymentIds: string[]): Promise<any> {
    return request('/payments/verify-bulk', shopId, {
      method: 'POST',
      body: JSON.stringify({paymentIds}),
    });
  },

  // ── Import ────────────────────────────────────────────────────────────────

  /**
   * Create members from a mapped spreadsheet.
   *
   * The rows are sent already mapped to member fields, because the mapping is
   * a decision a person made on screen and re-deriving it server-side from
   * their headings would be a second guess at the same question. `skipErrors`
   * carries the owner's answer to the preview: import what is good and hand
   * back the rest.
   */
  async importMembers(
    shopId: string,
    body: {
      rows: Record<string, string>[];
      mapping: Record<string, string>;
      skipErrors: boolean;
    },
  ): Promise<{added: number; skipped: {row: number; reason: string}[]}> {
    return request('/people/import', shopId, {method: 'POST', body: JSON.stringify(body)});
  },

  // ── Settings ──────────────────────────────────────────────────────────────

  /**
   * Payment methods, approval rules, reminders and the join form.
   *
   * These are fields of the WORKSPACE record; there is no `/settings` route.
   * The workspace id comes from the link — the API checks it against the link
   * and never trusts it as a way of naming someone else's business.
   */
  async getSettings(shopId: string, workspaceId: string): Promise<any> {
    return request(`/workspaces/${encodeURIComponent(workspaceId)}`, shopId);
  },

  /**
   * Merge-patch the workspace. Declared PUT, and the dashboard prefix also
   * accepts PATCH for it, which is the verb that matches what it does.
   */
  async updateSettings(
    shopId: string,
    workspaceId: string,
    patch: Record<string, any>,
  ): Promise<any> {
    return request(`/workspaces/${encodeURIComponent(workspaceId)}`, shopId, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  /** Staff and the staff invites still outstanding. */
  async listStaff(
    shopId: string,
    workspaceId: string,
  ): Promise<{items: any[]; invites: any[]}> {
    return request(`/workspaces/${encodeURIComponent(workspaceId)}/staff`, shopId);
  },

  /**
   * Invite a coach or front-desk user by email. Owner only.
   *
   * `POST /invites` is the MEMBER invite and is a different thing; staff go
   * through the workspace.
   */
  async inviteStaff(
    shopId: string,
    workspaceId: string,
    body: {email: string; role?: string},
  ): Promise<any> {
    return request(`/workspaces/${encodeURIComponent(workspaceId)}/staff`, shopId, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Take someone's access away. Owner only.
   *
   * There is no route that CHANGES a role: a role is chosen when the invite is
   * sent. The screen says so rather than offering a control that cannot save.
   */
  async removeStaff(shopId: string, workspaceId: string, userId: string): Promise<any> {
    return request(
      `/workspaces/${encodeURIComponent(workspaceId)}/staff/${encodeURIComponent(userId)}`,
      shopId,
      {method: 'DELETE'},
    );
  },
};

export default NetlyPayRepository;
